/**
 * SSE consumption (protocol/events.schema.json — the frozen frame contract).
 *
 * Two transports, per ARCHITECTURE §3.4 / api.md §10:
 *  - Chat: `POST /api/chat/sessions/{sid}/messages` responds
 *    `text/event-stream` directly on the POST → `consumeStream` (fetch +
 *    ReadableStream + eventsource-parser). A dropped connection is picked
 *    up again with `GET /stream` (`attachChatStream`, 2026-09-07): the turn
 *    keeps running on the server and replays what it emitted so far.
 *  - Jobs: `GET /api/jobs/{id}/events` → `subscribeJob` (native EventSource;
 *    auto-reconnect with Last-Event-ID replay for free).
 *
 * A frame is the `{type, data}` object of the schema; `event:` carries `type`
 * and `data:` carries the JSON `data`.
 */

import { createParser } from 'eventsource-parser';

import { API_BASE, errorFromResponse } from './client';
import type { ChatFrame, JobFrame, SseFrame } from './types';

export const CHAT_FRAME_TYPES = [
  'message.start',
  'text.delta',
  'thinking.delta',
  'tool.start',
  'tool.result',
  'diagram',
  'message.done',
  'error',
] as const;

export const JOB_FRAME_TYPES = [
  'job.phase',
  'job.plan',
  'job.node',
  'job.flow',
  'job.done',
] as const;

export type ChatFrameType = (typeof CHAT_FRAME_TYPES)[number];
export type JobFrameType = (typeof JOB_FRAME_TYPES)[number];

export function isChatFrameType(type: string): type is ChatFrameType {
  return (CHAT_FRAME_TYPES as readonly string[]).includes(type);
}

export function isJobFrameType(type: string): type is JobFrameType {
  return (JOB_FRAME_TYPES as readonly string[]).includes(type);
}

/** Terminal chat frames: every chat stream ends with exactly one of these. */
export function isTerminalChatFrame(frame: ChatFrame): boolean {
  return frame.type === 'message.done' || frame.type === 'error';
}

/**
 * Build a typed frame from a wire event. Returns null for unknown event
 * names or unparseable data (forward-compatibility: skip, never throw).
 */
export function parseFrame(eventName: string | undefined, dataText: string): SseFrame | null {
  if (eventName === undefined) return null;
  if (!isChatFrameType(eventName) && !isJobFrameType(eventName)) return null;
  let data: unknown;
  try {
    data = JSON.parse(dataText);
  } catch {
    return null;
  }
  if (typeof data !== 'object' || data === null) return null;
  return { type: eventName, data } as SseFrame;
}

export interface ConsumeStreamHandlers {
  /** Called once per frame, in wire order. */
  onFrame: (frame: ChatFrame) => void;
  /** Unknown event names (forward compat) — optional observability hook. */
  onUnknownFrame?: (eventName: string, dataText: string) => void;
}

export interface ConsumeStreamInit {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * Consume a chat SSE stream delivered on a fetch response body.
 *
 * - Non-2xx responses reject with `ApiClientError` (e.g. 409
 *   `generation_active` — an error envelope, not a stream).
 * - Resolves with the terminal frame (`message.done` | `error`) once the
 *   stream closes, or `null` if the connection closed without one (the turn
 *   keeps running on the server; `attachChatStream` picks it up again).
 * - Abort via `init.signal`; an abort resolves `null` rather than rejecting.
 */
export async function consumeStream(
  url: string,
  init: ConsumeStreamInit,
  handlers: ConsumeStreamHandlers,
): Promise<ChatFrame | null> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: init.method ?? 'POST',
      signal: init.signal ?? null,
      headers: {
        Accept: 'text/event-stream',
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
      body: init.body !== undefined ? JSON.stringify(init.body) : null,
    });
  } catch (err) {
    if (init.signal?.aborted) return null;
    throw err;
  }

  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  return consumeResponse(res, init.signal, handlers);
}

/**
 * Attach to the turn already running on a session (GET /stream, 2026-09-07):
 * the server replays every frame it emitted so far, then streams live. Resolves
 * `undefined` when nothing is running there (204), otherwise exactly like
 * `consumeStream`: the terminal frame, or `null` if the connection closed
 * without one.
 */
export async function attachChatStream(
  sessionId: string,
  handlers: ConsumeStreamHandlers,
  signal?: AbortSignal,
): Promise<ChatFrame | null | undefined> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/chat/sessions/${sessionId}/stream`, {
      method: 'GET',
      signal: signal ?? null,
      headers: { Accept: 'text/event-stream' },
    });
  } catch (err) {
    if (signal?.aborted) return null;
    throw err;
  }
  if (res.status === 204) return undefined;
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  return consumeResponse(res, signal, handlers);
}

/** Pump one SSE response body through `handlers` (shared by POST and attach). */
async function consumeResponse(
  res: Response,
  signal: AbortSignal | undefined,
  handlers: ConsumeStreamHandlers,
): Promise<ChatFrame | null> {
  if (res.body === null) {
    return null;
  }

  let terminal: ChatFrame | null = null;
  const parser = createParser({
    onEvent(event) {
      const frame = parseFrame(event.event, event.data);
      if (frame === null) {
        handlers.onUnknownFrame?.(event.event ?? '', event.data);
        return;
      }
      if (!isChatFrameType(frame.type)) {
        handlers.onUnknownFrame?.(frame.type, event.data);
        return;
      }
      const chatFrame = frame as ChatFrame;
      handlers.onFrame(chatFrame);
      if (isTerminalChatFrame(chatFrame)) {
        terminal = chatFrame;
      }
    },
  });

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
  } catch (err) {
    if (signal?.aborted) return terminal;
    throw err;
  } finally {
    reader.releaseLock();
  }
  return terminal;
}

/**
 * What a question is ABOUT, when it is about a region of a drawing the reader
 * picked out (round 8, DECISIONS #71). The text says "go deeper on these two
 * parts and leave the rest alone"; this is the same ask in a form the server
 * can act on: the mermaid the model last drew, and the labels to open or fold.
 *
 * Without it the model redraws the whole picture from memory and the promise
 * to keep everything else still is one it can only try to honour.
 *
 * Declared here rather than mirrored: api/types.ts is the contract mirror's
 * file and the send seam must not wait on it. Every field is a plain string
 * or array, so the mirror can replace this later without touching a caller.
 */
export interface DiagramRegionFocus {
  kind: 'diagram_region';
  /** the drawing on screen when the reader picked (server-minted id) */
  diagram_id: string;
  /** its mermaid source, verbatim, so the redraw can start from it */
  mermaid: string;
  /** the picked parts, in the drawing's own order */
  labels: string[];
  /** the round-8 More/Less ask; absent when the reader wrote the ask (DECISIONS #95) */
  direction?: 'more' | 'less';
}

/** Everything that may ride beside the text. One kind so far. */
export type MessageFocus = DiagramRegionFocus;

/** One `@` tag in the composer (DECISIONS #95): what the reader named. */
export interface MessageMention {
  kind: 'flow' | 'area' | 'entity' | 'file';
  repo_id: string;
  id: string;
  label: string;
}

/**
 * Send a chat message and stream the response (api.md §7).
 *
 * `focus` and `mentions` are omitted from the body entirely when there are
 * none, rather than sent as null or empty: a plain question must put exactly
 * the bytes on the wire it has always put there.
 */
export function streamChatMessage(
  sessionId: string,
  text: string,
  handlers: ConsumeStreamHandlers,
  signal?: AbortSignal,
  focus?: MessageFocus | null,
  mentions?: readonly MessageMention[] | null,
): Promise<ChatFrame | null> {
  const body: { text: string; focus?: MessageFocus; mentions?: MessageMention[] } = { text };
  if (focus !== undefined && focus !== null) body.focus = focus;
  if (mentions !== undefined && mentions !== null && mentions.length > 0) {
    body.mentions = [...mentions];
  }
  return consumeStream(
    `${API_BASE}/chat/sessions/${sessionId}/messages`,
    {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    },
    handlers,
  );
}

export interface JobSubscription {
  close: () => void;
}

export interface SubscribeJobHandlers {
  /** Called per frame in seq order; `seq` is the SSE `id:` (persisted seq, starts at 1). */
  onFrame: (frame: JobFrame, seq: number | null) => void;
  /** Fires on EventSource error state after the browser gives up reconnecting. */
  onConnectionError?: (ev: Event) => void;
}

/**
 * Subscribe to a job's progress stream with resume-for-free semantics:
 * the native EventSource sends `Last-Event-ID` on reconnect and the server
 * replays persisted events with seq > that value before live-tailing.
 * The subscription closes itself after the terminal `job.done` frame.
 */
export function subscribeJob(jobId: string, handlers: SubscribeJobHandlers): JobSubscription {
  const source = new EventSource(`${API_BASE}/jobs/${jobId}/events`);

  const listener = (ev: MessageEvent<string>) => {
    const frame = parseFrame(ev.type, ev.data);
    if (frame === null || !isJobFrameType(frame.type)) return;
    const seq = ev.lastEventId !== '' ? Number(ev.lastEventId) : null;
    handlers.onFrame(frame as JobFrame, Number.isNaN(seq) ? null : seq);
    if (frame.type === 'job.done') {
      source.close();
    }
  };

  for (const type of JOB_FRAME_TYPES) {
    source.addEventListener(type, listener as EventListener);
  }
  const onConnectionError = handlers.onConnectionError;
  if (onConnectionError !== undefined) {
    source.addEventListener('error', (ev) => {
      // The browser fires `error` on every reconnect attempt as well
      // (readyState CONNECTING, and it retries by itself); only a source
      // that has given up (CLOSED) has lost the connection.
      if (source.readyState === source.CLOSED) onConnectionError(ev);
    });
  }

  return {
    close: () => source.close(),
  };
}
